import './NewSearchSection.scss';
import { useContext } from 'react';
import { useAppSelector } from '../../../../Hooks/Hooks';
import { RootState } from '../../../../Redux/Store';
import { AppContext } from '../../../../Context/AppContext';
import { capitalizeFirstLetter, isEmptyObject } from '../../../../Utils/Basic';
import { COUNTRY, CANADA_STATES, STATES, stateLabels } from '../../../../Constants/Constants';
import { TNewSearchSectionProps } from './NewSearchSection.type';
import TypeAheadContainer from '../../../Shared/TypeAheadContainer/TypeAheadContainer';
import DatePickerComponent from '../../../Shared/DatePickerComponent/DatePickerComponent';
import ToggleSearchButton from '../../../Shared/ToggleSearchButton/ToggleSearchButton';
import SearchSelectList from '../../../SearchSelectList/SearchSelectList';
import SearchSlider from '../../Helper/SearchSlider/SearchSlider';

const NewSearchSection = (props: TNewSearchSectionProps) => {
  const {
    type,
    data,
    handleReset,
    handleLocationChange,
    handleRadiusChange,
    handleDateChange,
    isDestinationExpanded = false,
    handleDestinationExpansion,
    isStateSelectionEnabled = false,
    handleStateSelector,
    userInteractionWithDestination,
    handleInteractionWithDestination,
  } = props;

  const { isMobile } = useContext(AppContext);
  const searchQueryError: any = useAppSelector(
    (state: RootState) => state?.search?.searchQueryError,
  );

  const handleCountrySelectListChange = (type: string, target: any) => {
    const { value } = target;
    const updatedData = {
      ...data?.[`${type}Location`],
      State: [{}, {}, {}],
      ...value,
    };
    handleLocationChange(type, updatedData);
    return updatedData;
  };

  const handleStateSelectListChange = (type: string, target: any, index: any) => {
    const { name, value } = target;
    let state_Data = [...data?.[`${type}Location`]?.[name]];
    state_Data.splice(index, 1, value);
    const updatedData = {
      ...data?.[`${type}Location`],
      [name]: state_Data,
    };

    handleLocationChange(type, updatedData);
  };

  const states = () => {
    if (data?.destinationLocation?.CountryFullName === 'Canada') {
      return CANADA_STATES;
    }
    return STATES;
  };

  return (
    <div className={`NewSearchSection Search_Section_${capitalizeFirstLetter(type)}`}>
      <div className={`Label_Reset_Row ${type === 'origin' ? 'origin-margin' : ''}`}>
        <div>{type === 'origin' ? 'Pick-up' : 'Delivery'}</div>
        {!isMobile ? (
          <div onClick={() => handleReset(type)}>
            <img src="/oo/assets/icon/refresh.svg" alt="refresh" className="mr-2" />
            Reset
          </div>
        ) : type === 'origin' ? (
          <div>
            <span onClick={() => handleReset(type)}>Reset</span>
          </div>
        ) : isDestinationExpanded ? (
          <div>
            <span onClick={() => handleReset(type)}>Reset</span>
            <img
              src="/oo/assets/icon/minus-icon.svg"
              alt="minus"
              onClick={() => handleDestinationExpansion && handleDestinationExpansion(false)}
              className="ml-3"
            />
          </div>
        ) : (
          <div>
            <span
              onClick={() =>
                isDestinationExpanded
                  ? handleReset(type)
                  : handleDestinationExpansion && handleDestinationExpansion(true)
              }
            >
              {data?.destinationLocation?.City || 'Anywhere'}
            </span>
            <img
              src="/oo/assets/icon/plus.svg"
              alt="plus"
              className="ml-3"
              onClick={() => {
                handleDestinationExpansion && handleDestinationExpansion(true);
                handleInteractionWithDestination && handleInteractionWithDestination();
              }}
            />
          </div>
        )}
      </div>
      {isMobile && type === 'destination' && isDestinationExpanded && (
        <div className="State_City_Selection_Row mt-3">
          <div className="Type_Label">{capitalizeFirstLetter(type)}</div>
          <div className="State_City_Selection_Container">
            <span className="mr-2">Select States</span>
            <ToggleSearchButton
              onToggle={() => {
                handleReset(type);
                handleStateSelector && handleStateSelector();
              }}
              value={isStateSelectionEnabled}
            />
          </div>
        </div>
      )}
      {!isMobile && type === 'destination' && (
        <div className="State_City_Selection_Row">
          <span className="mr-2">Select States</span>
          <ToggleSearchButton
            onToggle={() => {
              handleReset(type);
              handleStateSelector && handleStateSelector();
            }}
            value={isStateSelectionEnabled}
          />
        </div>
      )}
      <div className="Search_Section_Right_Main">
        {type === 'destination' &&
        ((!isMobile && isStateSelectionEnabled) ||
          (isMobile && isDestinationExpanded && isStateSelectionEnabled)) ? (
          <div className="State_Selection_Container">
            <SearchSelectList
              key={`${data?.destinationLocation?.Country}`}
              options={COUNTRY}
              name={data?.destinationLocation?.Country || ''}
              handleChange={(target: any) => handleCountrySelectListChange(type, target)}
              value={data?.destinationLocation?.CountryFullName || ''}
              icon={true}
              type="Country"
            />
            <span className="Select_Label">Select 1 - 3 States/Provinces</span>
            {stateLabels?.map((State, _index) => (
              <SearchSelectList
                key={`${JSON.stringify(State)}-${_index}`}
                options={states()}
                name="State"
                type="State"
                value={!isEmptyObject(data?.[`${type}Location`]?.State?.[_index]) ? data?.[`${type}Location`]?.State?.[_index] : {name:"Select State "+(_index+1),value:"SelectState"}}
                handleChange={(target: any) => handleStateSelectListChange(type, target, _index)}
              />
            ))}
          </div>
        ) : (
          (!isMobile ||
            (isMobile &&
              (type === 'origin' || (type === 'destination' && isDestinationExpanded)))) && (
            <div className="Location_Radius_Container">
              {isMobile && type === 'origin' && (
                <div className="Type_Label">{capitalizeFirstLetter(type)}</div>
              )}
              <TypeAheadContainer
                type={type}
                data={data[`${type}Location`]}
                handleLocationChange={handleLocationChange}
                handleRadiusChange={handleRadiusChange}
              />
              <div className={`${capitalizeFirstLetter(type)}_Slider_Container`}>
                <SearchSlider
                  labelName={`${capitalizeFirstLetter(type)} Radius`}
                  InputValue={data[`${type}Radius`]}
                  name={`${type}Radius`}
                  configId={`${type}_RADIUS_INIT`}
                  handleChange={handleRadiusChange}
                  rangeDisabled={type === 'destination' && isEmptyObject(data[`${type}Location`])}
                />
              </div>
            </div>
          )
        )}
        {isMobile && (isDestinationExpanded || type === 'origin') && (
          <p className="Type_Label_Date">{type === 'origin' ? 'Pick-up Date' : 'Drop Date'}</p>
        )}
        {(isDestinationExpanded || type === 'origin' || !isMobile) && (
          <div className="From_To_Date">
            <div className={`${capitalizeFirstLetter(type)}_From_Date`}>
              <DatePickerComponent
                customClass={`${capitalizeFirstLetter(type)}_From_Date`}
                label="From Date:"
                type={`${type}FromDate`}
                value={data?.[`${type}FromDate`]}
                handleChange={handleDateChange}
                errorMessage={searchQueryError?.[`${type}FromDate`]}
                datePickerdisabled={
                  type === 'destination' &&
                  !userInteractionWithDestination &&
                  isEmptyObject(data?.destinationLocation)
                }
              />
            </div>
            <div className={`${capitalizeFirstLetter(type)}_To_Date`}>
              <DatePickerComponent
                customClass={`${capitalizeFirstLetter(type)}_To_Date`}
                label="To Date:"
                isToDate={true}
                type={`${type}ToDate`}
                value={data?.[`${type}ToDate`]}
                handleChange={handleDateChange}
                errorMessage={searchQueryError?.[`${type}ToDate`]}
                datePickerdisabled={
                  type === 'destination' &&
                  !userInteractionWithDestination &&
                  isEmptyObject(data?.destinationLocation)
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewSearchSection;
